import Link from 'next/link'
import Image from 'next/image'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { INSTALL_URL } from '@/lib/seo'

// Intrinsic dimensions of the 2026 wordmark asset — kept here so header and
// footer stay in the same aspect ratio without CLS.
const LOGO_W = 2372
const LOGO_H = 705

// Parent-brand wordmark, shown small beside the YES! logo as a "by allthe"
// endorsement lockup. Same asset the allthe platform ships.
const ALLTHE_W = 673
const ALLTHE_H = 226

export const BRAND = {
  coral: '#E55934',
  coralDark: '#c4441e',
  indigo: '#4357AD',
  indigoApp: '#4f46e5',
  indigoApp2: '#6366f1',
  indigoApp3: '#818cf8',
  violet: '#8b5cf6',
  violetBg: '#f5f3ff',
  teal: '#48A9A6',
  cream: '#EEE5E9',
  creamDeep: '#e4d9de',
  gold: '#F7C548',
  ink: '#0A0A0A',
  ink2: '#27272a',
  rule: 'rgba(20,20,20,0.08)',
  ruleSoft: 'rgba(20,20,20,0.06)',
  ruleStrong: 'rgba(20,20,20,0.16)',
  muted: '#52525B',
  mutedSoft: '#71717a',
  amber50: '#fffbeb',
  amber600: '#d97706',
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray900: '#111827',
} as const


type NavKey = 'home' | 'features' | 'how' | 'pricing' | 'docs' | 'about' | 'support' | 'privacy' | null

export function SiteHeader({ active = null }: { active?: NavKey }) {
  return (
    <>
    <a href="#main-content" className="skip-link">Skip to main content</a>
    <header style={{
      position: 'sticky', top: 0, zIndex: 50,
      background: 'rgba(255,255,255,0.78)',
      backdropFilter: 'saturate(180%) blur(14px)',
      WebkitBackdropFilter: 'saturate(180%) blur(14px)',
      borderBottom: `1px solid ${BRAND.rule}`,
    }}>
      <div className="max-w-[1200px] mx-auto px-5 sm:px-6 flex items-center justify-between gap-3" style={{ height: 64 }}>
        <Link href="/home" className="flex items-center gap-3 shrink-0" aria-label="YES! Personalized by allthe — home">
          <Image
            src="/yes-personalized-logo.png"
            alt="YES! Personalized"
            width={LOGO_W}
            height={LOGO_H}
            priority
            style={{ width: 'auto', height: 40 }}
          />
          <ByAllthe variant="dark" />
        </Link>

        <nav aria-label="Primary" className="hidden md:flex items-center gap-8 text-[14px]" style={{ color: BRAND.ink2, fontWeight: 500 }}>
          <NavLink href="/home#features" active={active === 'features'}>Features</NavLink>
          <NavLink href="/home#how-it-works" active={active === 'how'}>How it works</NavLink>
          <NavLink href="/home#pricing" active={active === 'pricing'}>Pricing</NavLink>
          <NavLink href="/docs" active={active === 'docs'}>Docs</NavLink>
          <NavLink href="/about" active={active === 'about'}>About</NavLink>
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <Link href="/support" className="hidden md:inline text-[14px] font-medium" style={{
            color: active === 'support' ? BRAND.indigo : BRAND.ink2,
          }}>
            Support
          </Link>
          <a
            href={INSTALL_URL}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: BRAND.ink, color: '#fff', fontWeight: 600,
              fontSize: 13, padding: '9px 14px', borderRadius: 999,
              whiteSpace: 'nowrap',
            }}
          >
            Install
            <ArrowRight size={13} strokeWidth={2.5} />
          </a>
        </div>
      </div>
    </header>
    </>
  )
}

/**
 * "by allthe" endorsement lockup.
 *
 * Sits beside the YES! wordmark, separated by a hairline so it reads as
 * attribution rather than part of the mark. Kept deliberately small — the
 * product brand leads, the parent brand endorses.
 */
function ByAllthe({ variant }: { variant: 'dark' | 'light' }) {
  const isDark = variant === 'dark'
  return (
    <span
      className="hidden sm:flex items-center gap-2 shrink-0"
      style={{
        paddingLeft: 12,
        borderLeft: `1px solid ${isDark ? BRAND.rule : 'rgba(255,255,255,0.14)'}`,
      }}
    >
      <span style={{
        fontSize: 10,
        letterSpacing: '0.08em',
        fontWeight: 500,
        color: isDark ? BRAND.mutedSoft : 'rgba(255,255,255,0.45)',
      }}>
        by
      </span>
      <Image
        src={isDark ? '/allthe-logo-dark.png' : '/allthe-logo-wht.png'}
        alt="allthe"
        width={ALLTHE_W}
        height={ALLTHE_H}
        style={{ width: 'auto', height: 12, opacity: isDark ? 0.75 : 0.7 }}
      />
    </span>
  )
}

function NavLink({ href, active, children }: { href: string; active?: boolean; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      className="hover:text-black transition-colors"
      style={{
        color: active ? BRAND.indigo : BRAND.ink2,
        fontWeight: active ? 600 : 500,
      }}
    >
      {children}
    </Link>
  )
}

export function SiteFooter() {
  return (
    <footer style={{ background: BRAND.ink, color: 'rgba(255,255,255,0.6)' }}>
      <div className="max-w-[1200px] mx-auto px-5 sm:px-6 pt-20 pb-12">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-5">
            <Link href="/home" className="inline-flex items-center gap-3 mb-5" aria-label="YES! Personalized by allthe — home">
              <Image
                src="/yes-personalized-logo-white.png"
                alt="YES! Personalized"
                width={LOGO_W}
                height={LOGO_H}
                style={{ width: 'auto', height: 34 }}
              />
              <ByAllthe variant="light" />
            </Link>
            <p style={{ fontSize: 15, lineHeight: 1.55, color: 'rgba(255,255,255,0.55)', maxWidth: 380 }}>
              A live personalisation widget, a real template editor, and print-ready artwork on every Shopify order.
            </p>
          </div>

          <div className="md:col-span-2 md:col-start-7">
            <FooterLabel>Product</FooterLabel>
            <ul className="space-y-2.5 text-[14px]">
              <li><Link href="/home#features" style={{ color: 'rgba(255,255,255,0.7)' }}>Features</Link></li>
              <li><Link href="/home#pricing" style={{ color: 'rgba(255,255,255,0.7)' }}>Pricing</Link></li>
              <li><Link href="/docs" style={{ color: 'rgba(255,255,255,0.7)' }}>Documentation</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <FooterLabel>Company</FooterLabel>
            <ul className="space-y-2.5 text-[14px]">
              <li><Link href="/about" style={{ color: 'rgba(255,255,255,0.7)' }}>About</Link></li>
              <li><Link href="/support" style={{ color: 'rgba(255,255,255,0.7)' }}>Support</Link></li>
              <li><Link href="/privacy" style={{ color: 'rgba(255,255,255,0.7)' }}>Privacy</Link></li>
              <li><Link href="/accessibility" style={{ color: 'rgba(255,255,255,0.7)' }}>Accessibility</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <FooterLabel>Get started</FooterLabel>
            <a
              href={INSTALL_URL}
              className="btn-coral"
              style={{ width: '100%', justifyContent: 'center' }}
            >
              Install on Shopify
              <ArrowRight size={14} strokeWidth={2.5} />
            </a>
          </div>
        </div>

        <div
          className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-8"
          style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
            © {new Date().getFullYear()} allthe ltd · YesPersonalized
          </div>
          <div style={{
            fontFamily: "'IBM Plex Sans', sans-serif",
            fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.3)',
          }}>
            Made for Shopify · Built in the UK
          </div>
        </div>
      </div>
    </footer>
  )
}

function FooterLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontFamily: "'IBM Plex Sans', sans-serif",
      fontSize: 10, letterSpacing: '0.18em',
      textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)',
      marginBottom: 16,
    }}>{children}</div>
  )
}

/**
 * Standard wrapper for marketing pages: SaaS-style sticky header + dark footer.
 * Pages render their own hero/content between them.
 */
export function MarketingChrome({
  children,
  active,
}: {
  children: React.ReactNode
  active?: NavKey
  // Legacy magazine-framing props (edition/strap/folio) are accepted but ignored
  // so existing call sites don't need updating in lockstep.
  edition?: string
  strap?: string
  folio?: string
}) {
  return (
    <div style={{
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      fontFamily: "'DM Sans', ui-sans-serif, system-ui, sans-serif",
      color: BRAND.ink,
    }}>
      <SiteHeader active={active} />
      <main id="main-content">{children}</main>
      <SiteFooter />
    </div>
  )
}
